"use client";

import { useContext, useMemo } from "react";
import Link from "next/link";
import Image from "next/image";
import { useSelectedLayoutSegment, usePathname } from "next/navigation";
import { useSession, signOut } from "next-auth/react";

import { docsConfig } from "@/config/docs";
import { marketingConfig } from "@/config/marketing";
import { siteConfig } from "@/config/site";
import { cn } from "@/lib/utils";
import { useScroll } from "@/hooks/use-scroll";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { ModalContext } from "@/components/modals/providers";
import { Icons } from "@/components/shared/icons";
import MaxWidthWrapper from "@/components/shared/max-width-wrapper";
import { TrialCTA } from "@/components/marketing/trial-cta";
import type { NavItem } from "@/types/index";
import { clearClientStorageOnLogout } from "@/lib/logout";

interface NavBarProps {
  scroll?: boolean;
  large?: boolean;
}

// Conversion landing pages render a stripped-down nav: just the logo and the
// single $1 trial button, so there are no exits before the decision.
const MINIMAL_NAV_PATHS = ["/start", "/products/options-journal"];

export function NavBar({ scroll = false }: NavBarProps) {
  const scrolled = useScroll(50);
  const { data: session, status } = useSession();
  const { setShowSignInModal } = useContext(ModalContext);

  const selectedLayout = useSelectedLayoutSegment();
  const pathname = usePathname();
  const documentation = selectedLayout === "docs";
  const isMinimal = !!pathname && MINIMAL_NAV_PATHS.includes(pathname);

  const configMap = {
    docs: docsConfig.mainNav,
  };

  const links =
    (selectedLayout && configMap[selectedLayout]) || marketingConfig.mainNav;

  if (isMinimal) {
    return (
      <header
        className={`sticky top-0 z-40 flex w-full justify-center bg-background/60 backdrop-blur-xl transition-all ${
          scroll ? (scrolled ? "border-b" : "bg-transparent") : "border-b"
        }`}
      >
        <MaxWidthWrapper className="flex h-14 items-center justify-between py-4">
          <Link href="/" className="flex items-center space-x-1.5">
            <Image
              src="/_static/landing/quantwheelicon-transparent.png"
              alt={siteConfig.name}
              width={40}
              height={40}
              className="h-8 w-8"
            />
            <span className="text-xl font-medium">{siteConfig.name}</span>
          </Link>

          <TrialCTA
            direct
            ctaLocation={`${pathname}_nav`}
            className="h-9 px-5 text-sm"
          >
            Start my $1 trial
          </TrialCTA>
        </MaxWidthWrapper>
      </header>
    );
  }

  return (
    <header
      className={`sticky top-0 z-40 flex w-full justify-center bg-background/60 backdrop-blur-xl transition-all ${
        scroll ? (scrolled ? "border-b" : "bg-transparent") : "border-b"
      }`}
    >
      <MaxWidthWrapper
        className="flex h-14 items-center justify-between py-4"
        large={documentation}
      >
        <div className="flex gap-6 md:gap-10">
          <Link href="/" className="flex items-center space-x-1.5">
            <Image 
              src="/_static/landing/quantwheelicon-transparent.png" 
              alt={siteConfig.name} 
              width={40} 
              height={40} 
              className="h-8 w-8"
            />
            <span className="text-xl font-medium">
              {siteConfig.name}
            </span>
          </Link>

          {links && links.length > 0 ? (
            <nav className="hidden gap-6 md:flex md:items-center">
              {links.map((item, index) => (
                item.items && item.items.length > 0 ? (
                  <NavDropdown key={index} item={item} />
                ) : (
                  <Link
                    key={index}
                    href={item.disabled ? "#" : item.href}
                    prefetch={true}
                    className={cn(
                      "flex items-center text-lg font-medium transition-colors hover:text-foreground/80 sm:text-sm",
                      item.href.startsWith(`/${selectedLayout}`)
                        ? "text-foreground"
                        : "text-foreground/60",
                      item.disabled && "cursor-not-allowed opacity-80",
                    )}
                  >
                    {item.title}
                  </Link>
                )
              ))}
            </nav>
          ) : null}
        </div>

        <div className="flex items-center space-x-3">
          {/* right header for docs */}
          {documentation ? (
            <div className="hidden flex-1 items-center space-x-4 sm:justify-end lg:flex">
              {/*<div className="hidden lg:flex lg:grow-0">
                <DocsSearch />
              </div>*/}
              <div className="flex lg:hidden">
                <Icons.search className="size-6 text-muted-foreground" />
              </div>
              {/*<div className="flex space-x-4">
                <Link
                  href={siteConfig.links.github}
                  target="_blank"
                  rel="noreferrer"
                >
                  <Icons.gitHub className="size-7" />
                  <span className="sr-only">GitHub</span>
                </Link>
              </div>*/}
            </div>
          ) : null}

          {session ? (
            <div className="hidden md:flex items-center space-x-2">
              {session.user?.email && (
                <span className="text-sm text-muted-foreground px-2">
                  {session.user.email}
                </span>
              )}
              <Link
                href="/dashboard"
              >
                <Button
                  className="gap-2 px-5"
                  variant="default"
                  size="sm"
                  rounded="full"
                >
                  <span>Dashboard</span>
                </Button>
              </Link>
              <Button
                className="gap-2 px-5"
                variant="outline"
                size="sm"
                rounded="full"
                onClick={() => {
                  clearClientStorageOnLogout();
                  signOut();
                }}
              >
                <Icons.logOut className="size-4" />
                <span>Log out</span>
              </Button>
            </div>
          ) : status === "unauthenticated" ? (
            <Button
              className="hidden gap-2 px-5 md:flex"
              variant="default"
              size="sm"
              rounded="full"
              onClick={() => setShowSignInModal(true)}
            >
              <span>Sign In</span>
              <Icons.arrowRight className="size-4" />
            </Button>
          ) : (
            <Skeleton className="hidden h-9 w-28 rounded-full lg:flex" />
          )}
        </div>
      </MaxWidthWrapper>
    </header>
  );
}

/** Modern grouped dropdown for Products/Tools nav items */
function NavDropdown({ item }: { item: NavItem }) {
  const subItems = item.items || [];
  const hasCategories = subItems.some((si) => si.category);

  // Group by category if categories exist
  const groups = useMemo(() => {
    if (!hasCategories) return [{ label: null, items: subItems }];
    const map = new Map<string, NavItem[]>();
    for (const si of subItems) {
      const cat = si.category || "Other";
      if (!map.has(cat)) map.set(cat, []);
      map.get(cat)!.push(si);
    }
    return Array.from(map.entries()).map(([label, items]) => ({ label, items }));
  }, [subItems, hasCategories]);

  return (
    <div className="relative group flex items-center">
      <button
        className={cn(
          "flex items-center gap-1 text-lg font-medium transition-colors hover:text-foreground/80 sm:text-sm",
          "text-foreground/60 h-full",
        )}
      >
        {item.title}
        <Icons.chevronDown className="size-3.5 transition-transform duration-200 group-hover:rotate-180 opacity-60" />
      </button>

      {/* Dropdown panel */}
      <div className="absolute left-1/2 -translate-x-1/2 top-full pt-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 ease-out">
        <div className={cn(
          "bg-background/95 backdrop-blur-xl border border-border/60 rounded-xl shadow-2xl shadow-black/20 overflow-hidden",
          hasCategories ? "min-w-[480px]" : "min-w-[320px]"
        )}>
          {groups.map((group, gi) => (
            <div key={gi}>
              {group.label && (
                <div className={cn(
                  "px-5 pt-4 pb-1.5 text-[11px] font-medium uppercase tracking-wider text-muted-foreground/60",
                  gi > 0 && "border-t border-border/40"
                )}>
                  {group.label}
                </div>
              )}
              <div className={cn(
                "grid gap-0.5 p-2",
                hasCategories ? "grid-cols-2" : "grid-cols-1"
              )}>
                {group.items.map((sub, si) => {
                  const Icon = sub.icon ? Icons[sub.icon] : null;
                  return (
                    <Link
                      key={si}
                      href={sub.disabled ? "#" : sub.href}
                      prefetch={true}
                      className={cn(
                        "flex items-start gap-3 rounded-lg px-3 py-2.5 transition-colors hover:bg-muted/60 group/item",
                        sub.disabled && "cursor-not-allowed opacity-50",
                      )}
                    >
                      {Icon && (
                        <div className="mt-0.5 p-1.5 rounded-md bg-primary/10 text-primary shrink-0">
                          <Icon className="size-4" />
                        </div>
                      )}
                      <div className="min-w-0">
                        <div className="flex items-center gap-1.5">
                          <span className="text-sm font-medium group-hover/item:text-foreground transition-colors">{sub.title}</span>
                          {sub.tag && (
                            <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-primary/15 text-primary leading-none">
                              {sub.tag}
                            </span>
                          )}
                        </div>
                        {sub.description && (
                          <p className="text-xs text-muted-foreground leading-snug mt-0.5">{sub.description}</p>
                        )}
                      </div>
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
